if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="GalleryDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Initialize dot navigation
        if (this.elements.dots && this.elements.viewer) {
          this.initDots();
        }

        // Desktop arrow navigation for thumbnail layouts.
        // SliderComponent scroll math breaks when non-active slides are
        // hidden (clientWidth 0), so we intercept the arrow clicks and
        // cycle through media items with setActiveMedia instead.
        if (this.elements.viewer && this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail')) {
          this.initDesktopArrows();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initDesktopArrows() {
        const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
        const nextBtn = this.elements.viewer.querySelector('button[name="next"]');
        if (!prevBtn || !nextBtn) return;

        // Hidden (display:none) slides report 0-width at offset 0, so SliderComponent
        // thinks the first slide is still visible and disables Previous.
        this.elements.viewer.enableSliderLooping = true;
        prevBtn.removeAttribute('disabled');
        nextBtn.removeAttribute('disabled');

        const handleArrow = (direction, event) => {
          if (!this.mql.matches) return; // let mobile use default scroll behaviour

          event.preventDefault();
          event.stopPropagation();

          const allItems = Array.from(
            this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
          );
          if (allItems.length < 2) return;

          const activeItem = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]')
            || allItems[0];
          const currentIndex = allItems.indexOf(activeItem);

          let nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
          if (nextIndex >= allItems.length) nextIndex = 0;
          if (nextIndex < 0) nextIndex = allItems.length - 1;

          const targetMediaId = allItems[nextIndex].dataset.mediaId;
          this.setActiveMedia(targetMediaId, false);
        };

        // Use capture phase so we run before SliderComponent's handler
        prevBtn.addEventListener('click', handleArrow.bind(this, 'prev'), true);
        nextBtn.addEventListener('click', handleArrow.bind(this, 'next'), true);
      }

      initDots() {
        if (!this.elements.dots || !this.elements.viewer) return;
        
        const dots = this.elements.dots.querySelectorAll('.gallery-dot');
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        
        if (dots.length === 0 || mediaItems.length === 0) return;
        
        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset.index, 10);
            if (index >= 0 && index < mediaItems.length) {
              const targetItem = mediaItems[index];
              const mediaId = targetItem.dataset.mediaId;
              if (mediaId) {
                this.setActiveMedia(mediaId, false);
              }
            }
          });
        });

        // Also listen for slide changes to update dots (works even without thumbnails)
        if (!this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce((event) => {
            const activeIndex = mediaItems.indexOf(event.detail.currentElement);
            this.setActiveDot(activeIndex);
          }, 500));
        }
      }

      setActiveDot(index) {
        if (!this.elements.dots) return;
        const dots = this.elements.dots.querySelectorAll('.gallery-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // Sync dots on slide change
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const activeIndex = mediaItems.indexOf(event.detail.currentElement);
        this.setActiveDot(activeIndex);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Sync dots when active media changes
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const activeIndex = mediaItems.indexOf(activeMedia);
        this.setActiveDot(activeIndex);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
